import type { ContextType } from 'react';

import type { BeforeInstallPromptEvent } from '@/constants';
import type { TrackingContext } from '@shared/tracking';

/**
 * Shows the browser's install prompt and tracks what the reader chose.
 *
 * Every call site used to fire `deferredPrompt.prompt()` and never read
 * `userChoice`, so installs were counted (`INSTALLED`) but a prompt the reader
 * dismissed left no trace -- install conversion couldn't be measured. All five
 * places that open the prompt go through here, so the outcome is tracked once.
 *
 * Not wrapped in a try/catch on purpose: a `BeforeInstallPromptEvent` can only
 * be prompted once, and a second `prompt()` rejecting should still surface
 * through the global error capture exactly as it did before this existed.
 */
export async function promptInstall(
	deferredPrompt: BeforeInstallPromptEvent,
	sendEvent: ContextType<typeof TrackingContext>['sendEvent'],
): Promise<void> {
	await deferredPrompt.prompt();
	const { outcome } = await deferredPrompt.userChoice;
	sendEvent({
		type:
			outcome === 'accepted'
				? 'INSTALL_PROMPT_ACCEPTED'
				: 'INSTALL_PROMPT_DISMISSED',
	});
}
